import { CHECKIN_FUTURE_CONFIRM_MS, epochOfLocalDateTime, formatMin, personalDateOf } from '@wiztime/core';
import type { CheckinStateChange } from '@wiztime/storage';
import { useEffect, useState, type ReactNode } from 'react';
import { setUnsavedEdit } from '../../../app/unsavedEdits';
import { useTranslation } from 'react-i18next';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useStore } from '../../../hooks/useServices';
import { cx } from '../../../lib/cx';
import { Button, ButtonVariant } from '../../../ui/Button';
import { ConfirmDialog } from '../../../ui/ConfirmDialog';
import { PlannedTimeField } from '../../checkin/PlannedTimeField';
import styles from './Cards.module.css';
import type { Occurrence } from './CheckinCard';
import { doneWindowStart, instantOfWallMinute, wallMinuteInWindow } from './doneTime';

/** One titled group of rows (yesterday's overdue doses, the day itself). */
export interface EditSection {
  readonly key: string;
  readonly title: string | null;
  /** Personal date the rows belong to. */
  readonly date: string;
  readonly rows: readonly Occurrence[];
}

interface RowDraft {
  readonly done: boolean;
  /** Wall-clock minute of the completion time, or null for "not recorded". */
  readonly minute: number | null;
  /** The time field was changed: otherwise a stored time outside the field's span is kept. */
  readonly edited: boolean;
  readonly invalid: boolean;
}

interface CheckinEditModeProps {
  readonly date: string;
  readonly dayStartMin: number;
  readonly nowMs: number;
  readonly sections: readonly EditSection[];
  readonly manageLink: ReactNode;
  readonly onDone: () => void;
}

/**
 * The check-in card's edit mode (checkin-occurrence-plan §3.5): every occurrence of the day,
 * faded ones and yesterday's overdue doses included, shows its completion box and time. Changes
 * stay in a draft until "Save", which writes them as one undo step; "Cancel" with changes asks
 * first. A time later than now blocks saving. Item settings are edited in Settings only.
 */
export function CheckinEditMode({
  date,
  dayStartMin,
  nowMs,
  sections,
  manageLink,
  onDone,
}: CheckinEditModeProps) {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const rows = sections.flatMap((section) =>
    section.rows.map((occurrence) => ({ occurrence, date: section.date })),
  );
  const windowOf = (occurrence: Occurrence, rowDate: string): number =>
    doneWindowStart(occurrence.occursAt, rowDate, dayStartMin);

  const initial = (occurrence: Occurrence, rowDate: string): RowDraft => {
    const stored = occurrence.doneAt === null ? null : Date.parse(occurrence.doneAt);
    const minute =
      stored === null || Number.isNaN(stored)
        ? null
        : wallMinuteInWindow(windowOf(occurrence, rowDate), stored);
    return { done: occurrence.done, minute, edited: false, invalid: false };
  };
  const [drafts, setDrafts] = useState<ReadonlyMap<string, RowDraft>>(
    () =>
      new Map(rows.map(({ occurrence, date: rowDate }) => [occurrence.key, initial(occurrence, rowDate)])),
  );
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);

  const draftOf = (occurrence: Occurrence, rowDate: string): RowDraft =>
    drafts.get(occurrence.key) ?? initial(occurrence, rowDate);
  const update = (key: string, next: RowDraft): void =>
    setDrafts((current) => new Map(current).set(key, next));

  /** Completion instant of a draft row, or null when not recorded. */
  const instantOf = (occurrence: Occurrence, rowDate: string, draft: RowDraft): number | null =>
    draft.minute === null ? null : instantOfWallMinute(windowOf(occurrence, rowDate), draft.minute);

  const changes: CheckinStateChange[] = rows.flatMap(({ occurrence, date: rowDate }) => {
    const draft = draftOf(occurrence, rowDate);
    if (draft.done === occurrence.done && !(draft.done && draft.edited)) return [];
    const ms = instantOf(occurrence, rowDate, draft);
    const doneAt = !draft.done
      ? null
      : draft.edited
        ? ms === null
          ? null
          : new Date(ms).toISOString()
        : occurrence.doneAt;
    if (draft.done === occurrence.done && doneAt === occurrence.doneAt) return [];
    return [{ itemId: occurrence.itemId, occurrence: occurrence.occurrence, done: draft.done, doneAt }];
  });
  // A newly ticked occurrence that has not come yet (later personal day, or over 12 hours ahead)
  // has no completion time to record: the card asks for it instead (plan §4).
  const today = personalDateOf(nowMs, dayStartMin);
  const notYet = (occurrence: Occurrence, rowDate: string): boolean =>
    rowDate > today ||
    (occurrence.occursAt !== null &&
      epochOfLocalDateTime(occurrence.occursAt) - nowMs > CHECKIN_FUTURE_CONFIRM_MS);
  const notYetKeys = new Set(
    rows.flatMap(({ occurrence, date: rowDate }) =>
      draftOf(occurrence, rowDate).done && !occurrence.done && notYet(occurrence, rowDate)
        ? [occurrence.key]
        : [],
    ),
  );
  const futureKeys = new Set(
    rows.flatMap(({ occurrence, date: rowDate }) => {
      const draft = draftOf(occurrence, rowDate);
      const ms = draft.done && draft.edited ? instantOf(occurrence, rowDate, draft) : null;
      return ms !== null && ms > nowMs ? [occurrence.key] : [];
    }),
  );
  const blocked =
    futureKeys.size > 0 ||
    notYetKeys.size > 0 ||
    rows.some(({ occurrence, date: rowDate }) => draftOf(occurrence, rowDate).invalid);
  const dirty = changes.length > 0;
  const [token] = useState(() => Symbol('checkin-edit'));
  useEffect(() => setUnsavedEdit(token, dirty), [token, dirty]);
  useEffect(() => () => setUnsavedEdit(token, false), [token]);

  const save = (): void => {
    if (blocked || saving) return;
    if (!dirty) {
      onDone();
      return;
    }
    setSaving(true);
    void run(() => store.setCheckinStates(changes)).then((result) => {
      setSaving(false);
      if (result.ok) onDone();
    });
  };
  const cancel = (): void => (dirty ? setConfirmDiscard(true) : onDone());

  /** Ticking a row with no time fills in the planned time when it has passed, else now. */
  const tick = (occurrence: Occurrence, rowDate: string, draft: RowDraft): RowDraft => {
    if (draft.minute !== null || occurrence.done) return { ...draft, done: true };
    const planned = occurrence.occursAt === null ? null : epochOfLocalDateTime(occurrence.occursAt);
    const ms = planned !== null && planned <= nowMs ? planned : nowMs;
    const minute = wallMinuteInWindow(windowOf(occurrence, rowDate), ms);
    return { ...draft, done: true, minute, edited: true };
  };

  const row = (occurrence: Occurrence, rowDate: string) => {
    const draft = draftOf(occurrence, rowDate);
    const plannedText = occurrence.plannedMin === null ? null : formatMin(occurrence.plannedMin);
    return (
      <li
        key={occurrence.key}
        className={cx(styles.editRow, draft.done && styles.checkRowDone)}
        data-testid="checkin-edit-row"
        data-item={occurrence.name}
        data-occurrence={occurrence.occurrence}
        data-done={draft.done}
      >
        <input
          type="checkbox"
          checked={draft.done}
          aria-label={t('checkin.editDoneAria', { name: occurrence.name })}
          onChange={() =>
            update(occurrence.key, draft.done ? { ...draft, done: false } : tick(occurrence, rowDate, draft))
          }
        />
        <span className={styles.planCol}>{plannedText ?? ''}</span>
        <span className={styles.checkName} title={occurrence.name}>
          {occurrence.name}
        </span>
        <PlannedTimeField
          value={draft.done ? draft.minute : null}
          itemName={occurrence.name}
          label={t('checkin.doneTimeLabel', { name: occurrence.name })}
          clearLabel={t('checkin.clearTime')}
          testId="checkin-edit-time"
          onCommit={(minute) =>
            // A time typed on an unticked row records it as done.
            update(occurrence.key, { ...draft, done: draft.done || minute !== null, minute, edited: true })
          }
          onInvalidChange={(invalid) =>
            // Reported after every render: an unchanged flag must keep the same state, or it loops.
            setDrafts((current) => {
              const held = current.get(occurrence.key) ?? initial(occurrence, rowDate);
              return held.invalid === invalid
                ? current
                : new Map(current).set(occurrence.key, { ...held, invalid });
            })
          }
        />
        {notYetKeys.has(occurrence.key) ? (
          <span className={styles.editError} role="alert" data-testid="checkin-edit-not-yet">
            {t('checkin.editNotYet')}
          </span>
        ) : futureKeys.has(occurrence.key) ? (
          <span className={styles.editError} role="alert" data-testid="checkin-edit-future">
            {t('checkin.doneTimeFuture')}
          </span>
        ) : null}
      </li>
    );
  };

  return (
    <div className={styles.editMode} data-testid="checkin-edit-mode" data-date={date}>
      {sections.map((section) =>
        section.rows.length === 0 ? null : (
          <section key={section.key}>
            {section.title === null ? null : <h3 className={styles.fadedTitle}>{section.title}</h3>}
            <ul className={styles.list}>{section.rows.map((occurrence) => row(occurrence, section.date))}</ul>
          </section>
        ),
      )}
      <p className={styles.mutedSmall}>
        {t('checkin.editHint')} {manageLink}
      </p>
      <div className={styles.editActions}>
        <Button
          variant={ButtonVariant.Ghost}
          data-testid="checkin-edit-cancel"
          onClick={cancel}
          disabled={saving}
        >
          {t('checkin.cancel')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          data-testid="checkin-edit-save"
          disabled={blocked || saving}
          onClick={save}
        >
          {t('checkin.save')}
        </Button>
      </div>
      {confirmDiscard ? (
        <ConfirmDialog
          title={t('checkin.discardTitle')}
          confirmLabel={t('checkin.discard')}
          cancelLabel={t('checkin.keepEditing')}
          danger
          testId="checkin-edit-discard"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={() => {
            setConfirmDiscard(false);
            onDone();
          }}
        >
          {t('checkin.discardBody', { count: changes.length })}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
